import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiRequestError } from '../lib/api';
import { devAuthEnabled } from '../lib/env';
import { usePortal } from './PortalContext';
import { PortalBrand } from './PortalBrand';
import { Button } from '../components/ui/Button';
import { FormField, Input } from '../components/ui/Field';
import { useFieldId } from '../components/ui/useFieldId';

/** What the visitor is told, by the status the API answered with - never the server's own text. */
export const PORTAL_COPY = {
  switchedOff:
    'The approval portal is not switched on for this site. Please contact the person who invited you.',
  tooManyAttempts: 'Too many attempts. Please wait a few minutes and try again.',
  badCode: 'That code is not valid or has expired. Request a new code.',
  incompleteLink: 'This link is incomplete. Please use the link you were sent.',
} as const;

/** The sentence for a refused request (403 portal off, 429 too many attempts), or null. */
function refusalCopy(err: unknown): string | null {
  if (!(err instanceof ApiRequestError)) return null;
  if (err.status === 403) return PORTAL_COPY.switchedOff;
  if (err.status === 429) return PORTAL_COPY.tooManyAttempts;
  return null;
}

/**
 * Magic-link landing. Accepts ?token= (deep link) or lets a stakeholder request
 * a 6-digit code by email and verify it. In dev, exposes a bypass button.
 */
export function PortalLanding() {
  const [params] = useSearchParams();
  const { signIn, enableDevBypass } = usePortal();
  // The link names the workspace (?tenant=); without it the API cannot know which one is meant.
  const tenant = params.get('tenant')?.trim() || undefined;

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'request' | 'verify'>('request');
  const [devCode, setDevCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailId = useFieldId('portal-email');
  const codeId = useFieldId('portal-code');

  // A ?token= deep link is treated as a pre-shared code for the given email.
  useEffect(() => {
    const token = params.get('token');
    if (token) {
      setCode(token);
      setStep('verify');
    }
  }, [params]);

  const requestCode = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.requestMagicLink(email.trim(), tenant);
      if (res.code) setDevCode(res.code);
      setStep('verify');
    } catch (err) {
      setError(
        refusalCopy(err) ?? (err instanceof ApiRequestError ? err.message : 'Could not send code'),
      );
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.verifyMagicLink(email.trim(), code.trim(), tenant);
      signIn(res.accessToken, res.principal, tenant ?? res.principal.activeTenantId ?? null);
    } catch (err) {
      setError(
        refusalCopy(err) ??
          (err instanceof ApiRequestError && err.status === 401
            ? PORTAL_COPY.badCode
            : err instanceof ApiRequestError
              ? err.message
              : 'Invalid or expired code'),
      );
    } finally {
      setBusy(false);
    }
  };

  // Back to the first step: the email can be changed again and a fresh code asked for.
  const startOver = () => {
    setCode('');
    setDevCode(null);
    setError(null);
    setStep('request');
  };

  return (
    // Scroll on the OUTER box and centre on an inner `min-h-full` one: centring a taller-than-
    // viewport child directly in a scroll container clips its TOP beyond reach (flexbox centring
    // overflows both ways). This way it centres when short and scrolls when tall.
    <div className="h-full overflow-y-auto bg-page">
      <div className="flex min-h-full items-center justify-center px-4 py-8">
        <div className="w-full max-w-md rounded-2xl border border-line bg-card p-8 shadow-panel">
        <div className="mb-6">
          <PortalBrand label="Approvals" />
        </div>

        <h1 className="text-xl font-semibold text-ink">Review &amp; approve content</h1>
        <p className="mt-1 text-sm text-ink-muted">
          {step === 'request'
            ? 'Enter your email and we’ll send you a 6-digit access code.'
            : 'If that email has been invited to review for this workspace, a 6-digit code is on its way. It expires in 15 minutes.'}
        </p>

        <div className="mt-6 space-y-4">
          <FormField label="Email" htmlFor={emailId}>
            <Input
              id={emailId}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={step === 'verify' || !tenant}
              placeholder="you@company.com"
            />
          </FormField>

          {step === 'verify' && (
            <FormField label="Access code" htmlFor={codeId} hint={devCode ? `Dev code: ${devCode}` : undefined}>
              <Input
                id={codeId}
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={!tenant}
                placeholder="123456"
              />
            </FormField>
          )}

          {!tenant ? (
            <p className="text-sm text-red-600 dark:text-error-dark">{PORTAL_COPY.incompleteLink}</p>
          ) : (
            error && <p className="text-sm text-red-600 dark:text-error-dark">{error}</p>
          )}

          {step === 'request' ? (
            <Button
              className="w-full"
              loading={busy}
              disabled={!email.trim() || !tenant}
              onClick={requestCode}
            >
              Send code
            </Button>
          ) : (
            <>
              <Button
                className="w-full"
                loading={busy}
                disabled={!code.trim() || !tenant}
                onClick={verify}
              >
                Verify &amp; continue
              </Button>
              <button
                type="button"
                onClick={startOver}
                disabled={busy}
                className="focus-ring mx-auto block rounded text-sm font-medium text-link hover:text-action-hover hover:underline disabled:opacity-50"
              >
                Request a new code
              </button>
            </>
          )}

          {devAuthEnabled && (
            <button
              type="button"
              onClick={enableDevBypass}
              className="focus-ring w-full rounded-lg border border-dashed border-line-dashed py-2 text-xs font-medium text-ink-muted hover:bg-hover"
            >
              Dev: skip sign-in and view portal
            </button>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
